import { createSelector } from "reselect";

import {
  selectOrganizationConfigDetailsByEntityId,
  selectOrganizationConfigDetailsBySelectedEntityId,
} from "../organizationConfig/selectors";
import {
  selectWorkspaceConfigDetailsByEntityId,
  selectWorkspaceConfigDetailsBySelectedEntityId,
} from "../workspaceConfig/selectors";

export const selectEntityConfigDetailsBySelectedEntityId = createSelector(
  selectOrganizationConfigDetailsBySelectedEntityId,
  selectWorkspaceConfigDetailsBySelectedEntityId,
  (organizationConfigDetails, workspaceConfigDetails) => organizationConfigDetails || workspaceConfigDetails || null,
);

export const selectEntityConfigDetailsByEntityId = createSelector(
  selectOrganizationConfigDetailsByEntityId,
  selectWorkspaceConfigDetailsByEntityId,
  (organizationConfigDetails, workspaceConfigDetails) => organizationConfigDetails || workspaceConfigDetails || null,
);

const selectCategoryConfiguration = (clientSettings) => clientSettings?.category_configuration || [];

export const selectCategoryConfigurationBySelectedUserId = createSelector(
  selectEntityConfigDetailsBySelectedEntityId,
  selectCategoryConfiguration,
);

export const selectClientWebhookUrlsBySelectedUserId = createSelector(
  selectEntityConfigDetailsBySelectedEntityId,
  (clientSettings) => clientSettings?.webhook?.clientWebHookURLs || [],
);

export const selectClientWebhookUrlsByEntityId = createSelector(
  selectEntityConfigDetailsByEntityId,
  (clientSettings) => clientSettings?.webhook?.clientWebHookURLs || [],
);

export const selectConfigurationByCategory = createSelector(
  selectCategoryConfigurationBySelectedUserId,
  (_, props) => props.category,
  (categoryConfiguration, category) => categoryConfiguration.find((config) => config?.category === category) || null,
);

const selectClipTaggingFields = (configurationByCategory) => configurationByCategory?.clipTaggingFields || [];

export const selectClipTaggingFieldsByConfigurationCategoryFromClientSettings = createSelector(
  selectConfigurationByCategory,
  selectClipTaggingFields,
);

const selectMisObject = (clientSettings) => clientSettings?.misObject || {};

export const selectMisObjectBySelectedUserId = createSelector(
  selectEntityConfigDetailsBySelectedEntityId,
  selectMisObject,
);

const selectMisObjectPlayerIds = (misObject) => misObject?.playerIds || [];

export const selectMisObjectPlayerIdsBySelectedUserId = createSelector(
  selectMisObjectBySelectedUserId,
  selectMisObjectPlayerIds,
);

const selectPermissions = (clientSettings) => clientSettings?.permissions || {};

export const selectPermissionsBySelectedEntityId = createSelector(
  selectEntityConfigDetailsBySelectedEntityId,
  selectPermissions,
);

export const selectPermissionsByEntityId = createSelector(selectEntityConfigDetailsByEntityId, selectPermissions);

export const selectClientTypeBySelectedUserId = createSelector(
  selectEntityConfigDetailsBySelectedEntityId,
  (clientSettings) => clientSettings?.clientType || "",
);

export const selectLimitationBySelectedUserId = createSelector(
  selectEntityConfigDetailsBySelectedEntityId,
  (clientSettings) => clientSettings?.limitation,
);

export const selectEntityConfigLimitationBySelectedEntityId = createSelector(
  selectEntityConfigDetailsBySelectedEntityId,
  (entityConfigDetails) => entityConfigDetails?.limitation,
);

export const selectEntityConfigPermissionBySelectedEntityId = createSelector(
  selectEntityConfigDetailsBySelectedEntityId,
  (entityConfigDetails) => entityConfigDetails?.permissions,
);
export const selectLimitationMaxVideoProcessDurationBySelectedUserId = createSelector(
  selectLimitationBySelectedUserId,
  (limitation) => limitation?.maxVideoProcessDuration || 0,
);

const selectAppAdditionalPermissionsZeepublishing = (appAdditionalPermissions) =>
  appAdditionalPermissions?.zeepublishing || false;

export const selectAppAdditionalPermissionsZeepublishingBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsZeepublishing,
);
export const selectAppAdditionalPermissionsAllowDirectDownload = createSelector(
  selectPermissionsBySelectedEntityId,
  (permissions) => permissions.allowDirectDownload,
);

export const selectIsTV2PlayerSyncBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  (permissions) => permissions.isTV2PlayerSync,
);

const selectAppAdditionalPermissionsClipZipPackageDownload = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isPackageDownload || false;

export const selectAppAdditionalPermissionsClipZipPackageDownloadBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsClipZipPackageDownload,
);

const selectAppAdditionalPermissionsAdvanceEditor = (appAdditionalPermissions) =>
  appAdditionalPermissions?.advanceEditor || false;

export const selectAppAdditionalPermissionsAdvanceEditorBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsAdvanceEditor,
);

const selectAppAdditionalPermissionsExportDataFeedJSON = (appAdditionalPermissions) =>
  appAdditionalPermissions?.exportDataFeedJSON || false;

export const selectAppAdditionalPermissionsExportDataFeedJSONBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsExportDataFeedJSON,
);

const selectAppAdditionalPermissionsIsStreamPointerLiveVideo = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isStreamPointerLiveVideo || false;

export const selectAppAdditionalPermissionsIsStreamPointerLiveVideoBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsStreamPointerLiveVideo,
);

const selectAppAdditionalPermissionsIsStreamPointerInternalDatafeed = (appAdditionalPermissions) =>
  appAdditionalPermissions?.internalDatafeedMarker || false;

export const selectAppAdditionalPermissionsIsStreamPointerInternalDatafeedBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsStreamPointerInternalDatafeed,
);

const selectAppAdditionalPermissionsTvcomChanges = (appAdditionalPermissions) =>
  appAdditionalPermissions?.tvcomChanges || false;

export const selectAppAdditionalPermissionsTvcomChangesBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsTvcomChanges,
);

const selectAppAdditionalPermissionsClipRating = (appAdditionalPermissions) =>
  appAdditionalPermissions?.clipRating || false;

export const selectAppAdditionalPermissionsClipRatingBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsClipRating,
);

const selectAppAdditionalPermissionsAllowAutoRating = (appAdditionalPermissions) =>
  appAdditionalPermissions?.allowAutoRating || false;

export const selectAppAdditionalPermissionsAllowAutoRatingBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsAllowAutoRating,
);

const selectAppAdditionalPermissionsAllowPartnerApi = (appAdditionalPermissions) =>
  appAdditionalPermissions?.allowPartnerApi || false;

export const selectAppAdditionalPermissionsAllowPartnerApiBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsAllowPartnerApi,
);

const selectAppAdditionalPermissionsFolderRating = (appAdditionalPermissions) =>
  appAdditionalPermissions?.folderRating || false;

export const selectAppAdditionalPermissionsFolderRatingBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsFolderRating,
);

const selectAppAdditionalPermissionsTriggerAutoflip = (appAdditionalPermissions) =>
  appAdditionalPermissions?.triggerAutoflip || false;

export const selectAppAdditionalPermissionsTriggerAutoflipBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsTriggerAutoflip,
);

const selectAppAdditionalPermissionsVod = (appAdditionalPermissions) => appAdditionalPermissions?.vod || false;

export const selectAppAdditionalPermissionsVodBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsVod,
);

const selectAppAdditionalPermissionAllowEnhancedTagging = (appAdditionalPermissions) =>
  appAdditionalPermissions?.allowEnhancedTagging || false;

export const selectAppAdditionalPermissionAllowEnhancedTaggingBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionAllowEnhancedTagging,
);

const selectAppAdditionalPermissionFancodeChanges = (appAdditionalPermissions) =>
  appAdditionalPermissions?.fancodeChanges || false;

export const selectAppAdditionalPermissionFancodeChangesBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionFancodeChanges,
);

const selectAppAdditionalPermissionsDownloadClipButton = (appAdditionalPermissions) =>
  appAdditionalPermissions?.downloadClipButton || false;

export const selectAppAdditionalPermissionsDownloadClipButtonBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsDownloadClipButton,
);

const selectAppAdditionalPermissionsDownloadHighlightButton = (appAdditionalPermissions) =>
  appAdditionalPermissions?.downloadHighlightButton || false;

export const selectAppAdditionalPermissionsDownloadHighlightButtonBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsDownloadHighlightButton,
);

const selectAppAdditionalPermissionsIsHideRightClick = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isHideRightClick ?? true;

export const selectAppAdditionalPermissionsIsHideRightClickBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsHideRightClick,
);

const selectAppAdditionalPermissionsIsEditVideo = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isEditVideo ?? true;

export const selectAppAdditionalPermissionsIsEditVideoBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsEditVideo,
);

const selectPermissionsReviewerFunctionality = (permissions) => permissions?.reviewerFunctionality || false;

export const selectPermissionReviewerFunctionalityBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectPermissionsReviewerFunctionality,
);

const selectAppAdditionalPermissionsExportStreamJSON = (appAdditionalPermissions) =>
  appAdditionalPermissions?.exportStreamJSON || false;

export const selectAppAdditionalPermissionsExportStreamJSONBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsExportStreamJSON,
);

const selectAppAdditionalPermissionsIsDeleteVideo = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isDeleteVideo ?? true;

export const selectAppAdditionalPermissionsIsDeleteVideoBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsDeleteVideo,
);

const selectAppAdditionalPermissionsExportFolderJSON = (appAdditionalPermissions) =>
  appAdditionalPermissions?.exportFolderJSON || false;

export const selectAppAdditionalPermissionsExportFolderJSONBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsExportFolderJSON,
);

const selectAppAdditionalPermissionsAddNewVideo = (appAdditionalPermissions) =>
  appAdditionalPermissions?.addNewVideo ?? true;

export const selectAppAdditionalPermissionsAddNewVideoBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsAddNewVideo,
);

const selectAppAdditionalPermissionsExportStreamClipsXLSX = (appAdditionalPermissions) =>
  appAdditionalPermissions?.exportStreamClipsXLSX || false;

export const selectAppAdditionalPermissionsExportStreamClipsXLSXBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsExportStreamClipsXLSX,
);

const selectAppAdditionalPermissionsExportMatchReport = (appAdditionalPermissions) =>
  appAdditionalPermissions?.exportMatchReport || false;

export const selectAppAdditionalPermissionsExportMatchReportBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsExportMatchReport,
);

const selectAppAdditionalPermissionsExportEventsJSON = (appAdditionalPermissions) =>
  appAdditionalPermissions?.exportEventsJSON || false;

export const selectAppAdditionalPermissionsExportEventsJSONBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsExportEventsJSON,
);

const selectAppAdditionalPermissionsIsReTriggerVideo = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isRetriggerVideo ?? true;

export const selectAppAdditionalPermissionsIsReTriggerVideoBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsReTriggerVideo,
);

const selectAppAdditionalPermissionsIsHlsToMp4 = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isHlsToMp4 || false;

export const selectAppAdditionalPermissionsIsHlsToMp4BySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsHlsToMp4,
);

const selectAppAdditionalPermissionsIsViewVideoDetails = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isViewVideoDetails ?? true;

export const selectAppAdditionalPermissionsIsViewVideoDetailsBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsViewVideoDetails,
);

const selectAppAdditionalPermissionsPublishToCMS = (appAdditionalPermissions) =>
  appAdditionalPermissions?.publishToCMS || false;

export const selectAppAdditionalPermissionsPublishToCMSBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsPublishToCMS,
);

const selectAppAdditionalPermissionsExportClipJson = (appAdditionalPermissions) =>
  appAdditionalPermissions?.exportClipJSON || false;

const selectAppAdditionalPermissionsExportClipEDL = (appAdditionalPermissions) =>
  appAdditionalPermissions?.exportEdl || false;

export const selectAppAdditionalPermissionsExportClipJsonBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsExportClipJson,
);

export const selectAppAdditionalPermissionsExportClipEDLBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsExportClipEDL,
);

export const selectAppAdditionalPermissionsDatafeedExportClipJsonBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsExportClipJson,
);

const selectAppAdditionalSocialMediaPublish = (appAdditionalPermissions) =>
  appAdditionalPermissions?.socialMediaPublish || false;

export const selectAppAdditionalSocialMediaPublishBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalSocialMediaPublish,
);

const selectAppAdditionalUploadToCloud = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isUploadToCloud ?? true;

export const selectAppAdditionalUploadToCloudBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalUploadToCloud,
);

const selectAppAdditionalUploadToFTP = (appAdditionalPermissions) => appAdditionalPermissions?.uploadToFTP || false;

export const selectAppAdditionalUploadToFTPBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalUploadToFTP,
);

const selectAppAdditionalUploadToSFTP = (appAdditionalPermissions) => appAdditionalPermissions?.uploadToSFTP || false;

export const selectAppAdditionalUploadToSFTPBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalUploadToSFTP,
);

const selectAppAdditionalUploadToAspera = (appAdditionalPermissions) =>
  appAdditionalPermissions?.uploadToAspera || false;

export const selectAppAdditionalUploadToAsperaBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalUploadToAspera,
);

const selectAppAdditionalUploadToBrightCove = (appAdditionalPermissions) =>
  appAdditionalPermissions?.uploadtoBrightcove || false;

export const selectAppAdditionalUploadToBrightCoveBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalUploadToBrightCove,
);

const selectAppAdditionalPublishToEmerce = (appAdditionalPermissions) =>
  appAdditionalPermissions?.publishToEmerce || false;

export const selectAppAdditionalPublishToEmerceBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPublishToEmerce,
);

const selectAppAdditionalPublishToStorifyMe = (appAdditionalPermissions) =>
  appAdditionalPermissions?.publishToStorifyMe || false;

export const selectAppAdditionalPublishToStorifyMeBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPublishToStorifyMe,
);

const selectAppAdditionalPermissionsChangeClipsThumbnail = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isChangeClipsThumbnail ?? true;

export const selectAppAdditionalPermissionsChangeClipsThumbnailBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsChangeClipsThumbnail,
);

const selectAppAdditionalPermissionsGenerateClipTitle = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isGenerateClipTitle ?? true;

export const selectAppAdditionalPermissionsGenerateClipTitleBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsGenerateClipTitle,
);

const selectAppAdditionalPermissionsExportXML = (appAdditionalPermissions) =>
  appAdditionalPermissions?.exportXMP ?? false;

export const selectAppAdditionalPermissionsExportXMLBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsExportXML,
);

const selectAppAdditionalPermissionsIsClipDelete = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isClipDelete ?? true;

export const selectAppAdditionalPermissionsClipDeleteBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsClipDelete,
);

const selectAppAdditionalPermissionsStaticCropper = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isStaticCropper ?? true;

export const selectAppAdditionalPermissionsStaticCropperBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsStaticCropper,
);

const selectAppAdditionalPermissionsDynamicCropper = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isDynamicCropper ?? true;

export const selectAppAdditionalPermissionsDynamicCropperBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsDynamicCropper,
);

const selectAppAdditionalPermissionsRuleManagement = (appAdditionalPermissions) =>
  appAdditionalPermissions?.ruleManagement || false;

export const selectAppAdditionalPermissionsRuleManagementBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsRuleManagement,
);

const selectClientSettingsIsProcessingLimit = (clientSettings) =>
  clientSettings?.permissions?.isProcessingLimit || false;

export const selectClientSettingsIsProcessingLimitBySelectedUserId = createSelector(
  selectEntityConfigDetailsBySelectedEntityId,
  selectClientSettingsIsProcessingLimit,
);

const selectAppAdditionalPermissionsIsAmplify = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isAmplify || false;

export const selectAppAdditionalPermissionsIsAmplifyBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsAmplify,
);

const selectAppAdditionalPermissionsBumperExclusionFromDownloadLimit = (appAdditionalPermissions) =>
  appAdditionalPermissions?.bumperExclusionFromDownloadLimit || false;

export const selectAppAdditionalPermissionsBumperExclusionFromDownloadLimitBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsBumperExclusionFromDownloadLimit,
);

const selectAppAdditionalPermissionsIsZeeEntertainmentAutoFlip = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isZeeEntertainmentAutoflip || false;

export const selectAppAdditionalPermissionsIsZeeEntertainmentAutoFlipBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsZeeEntertainmentAutoFlip,
);

const selectAppAdditionalPermissionIsCopyClip = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isCopyClip ?? true;

export const selectAppAdditionalPermissionIsCopyClipBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionIsCopyClip,
);

const selectAppAdditionalPermissionSonyFeed = (appAdditionalPermissions) => appAdditionalPermissions?.sonyFeed || false;

export const selectAppAdditionalPermissionSonyFeedBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionSonyFeed,
);

const selectAppAdditionalPermissionDynamicCms = (appAdditionalPermissions) =>
  appAdditionalPermissions?.dynamicCms || false;

export const selectAppAdditionalPermissionDynamicCmsBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionDynamicCms,
);

const selectAppAdditionalPermissionExportUsageReport = (appAdditionalPermissions) =>
  appAdditionalPermissions?.exportUsageReport || false;

export const selectAppAdditionalPermissionExportUsageReportBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionExportUsageReport,
);

const selectAppAdditionalPermissionShowArchival = (appAdditionalPermissions) =>
  appAdditionalPermissions?.showArchival || false;

export const selectAppAdditionalPermissionShowArchivalBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionShowArchival,
);

const selectAppAdditionalPermissionShowAutomatedClipTitle = (appAdditionalPermissions) =>
  appAdditionalPermissions?.automatedClipTitleGeneration || false;

export const selectAppAdditionalPermissionShowAutomatedClipTitleBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionShowAutomatedClipTitle,
);

const selectAppAdditionalPermissionIsAllowAccessToArchiveVideos = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isAllowAccessToArchiveVideos ?? true;

export const selectAppAdditionalPermissionIsAllowAccessToArchiveVideosBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionIsAllowAccessToArchiveVideos,
);

const selectAppAdditionalPermissionMultiFolderUpload = (appAdditionalPermissions) =>
  appAdditionalPermissions?.multiFolderUpload || false;

export const selectAppAdditionalPermissionMultiFolderUploadBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionMultiFolderUpload,
);

const selectAppAdditionalPermissionShowWorkspaceStreams = (appAdditionalPermissions) =>
  appAdditionalPermissions?.showWorkspaceStreams || false;

export const selectAppAdditionalPermissionShowWorkspaceStreamsBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionShowWorkspaceStreams,
);

const selectAppAdditionalPermissionDisablePublishWithoutTags = (appAdditionalPermissions) =>
  appAdditionalPermissions?.disablePublishWithoutTags || false;

export const selectAppAdditionalPermissionDisablePublishWithoutTagsBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionDisablePublishWithoutTags,
);

const selectAppAdditionalPermissionIsThirdPartyS3 = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isThirdPartyS3 || false;

export const selectAppAdditionalPermissionIsThirdPartyS3BySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionIsThirdPartyS3,
);

const selectAppAdditionalPermissionIsAllowAccessToArchiveClips = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isAllowAccessToArchiveClips ?? true;

export const selectAppAdditionalPermissionIsAllowAccessToArchiveClipsBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionIsAllowAccessToArchiveClips,
);

const selectClientSettingsIsAutoFlipEnable = (clientSettings) => clientSettings?.permissions?.isAutoFlipEnable || false;

export const selectClientSettingsIsAutoFlipEnableBySelectedUserId = createSelector(
  selectEntityConfigDetailsBySelectedEntityId,
  selectClientSettingsIsAutoFlipEnable,
);

const selectAppAdditionalPermissionMultipleStoragePath = (appAdditionalPermissions) =>
  appAdditionalPermissions?.multipleStoragePath ?? false;

export const selectAppAdditionalPermissionMultipleStoragePathBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionMultipleStoragePath,
);

const selectClientSettingsAddStorageButton = (clientSettings) => clientSettings?.permissions?.addStorageButton || false;

export const selectClientSettingsAddStorageButtonBySelectedUserId = createSelector(
  selectEntityConfigDetailsBySelectedEntityId,
  selectClientSettingsAddStorageButton,
);

const selectEntityMaxNumberOfUsers = (configDetails) => configDetails?.maxNumberOfUsers || 0;

export const selectEntityMaxNumberOfUsersBySelectedEntityId = createSelector(
  selectEntityConfigDetailsBySelectedEntityId,
  selectEntityMaxNumberOfUsers,
);

export const selectAllCustomFields = createSelector(
  selectEntityConfigDetailsByEntityId,
  (configDetails) => configDetails?.customFields || [],
);

export const selectCustomFieldsOptions = createSelector(selectAllCustomFields, (customFields) =>
  customFields
    ?.map((customField) => {
      if (customField?.inputFieldLabel && customField?.inputFieldName) {
        return {
          label: customField?.inputFieldLabel,
          value: customField?.inputFieldName,
        };
      }

      return null;
    })
    .filter(Boolean),
);

export const selectEntityMaxNumberOfUsersByEntityId = createSelector(
  selectEntityConfigDetailsByEntityId,
  selectEntityMaxNumberOfUsers,
);

const selectAppAdditionalPermissionAllowEnStorytellerIntegration = (appAdditionalPermissions) =>
  appAdditionalPermissions?.allowStorytellerIntegration || false;

export const selectAppAdditionalPermissionAllowStorytellerIntegrationBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionAllowEnStorytellerIntegration,
);

const selectAppAdditionalPermissionAllowEnStorifyMeIntegration = (appAdditionalPermissions) =>
  appAdditionalPermissions?.allowStorifyMeIntegration || false;

export const selectAppAdditionalPermissionAllowStorifyMeIntegrationBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionAllowEnStorifyMeIntegration,
);

const selectAppAdditionalPermissionAllowEnDailyMotionIntegration = (appAdditionalPermissions) =>
  appAdditionalPermissions?.allowDailyMotionIntegration || false;

export const selectAppAdditionalPermissionAllowDailyMotionIntegrationBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionAllowEnDailyMotionIntegration,
);

const selectAppAdditionalPermissionAllowOnedriveIntegration = (appAdditionalPermissions) =>
  appAdditionalPermissions?.allowOnedriveIntegration || false;

export const selectAppAdditionalPermissionAllowOnedriveIntegrationBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionAllowOnedriveIntegration,
);

const selectAppAdditionalPermissionAllowGoogleDriveIntegration = (appAdditionalPermissions) =>
  appAdditionalPermissions?.allowGoogleDriveIntegration || false;

export const selectAppAdditionalPermissionAllowGoogleDriveIntegrationBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionAllowGoogleDriveIntegration,
);

const selectAppAdditionalPermissionAllowFrameIOIntegration = (appAdditionalPermissions) =>
  appAdditionalPermissions?.allowFrameIOIntegration || false;

export const selectAppAdditionalPermissionAllowFrameIOIntegrationBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionAllowFrameIOIntegration,
);

export const selectAppAdditionalPermissionsOutputVideoTemplate = (appAdditionalPermissions) =>
  appAdditionalPermissions?.outputVideoTemplate || false;

export const selectAppAdditionalPermissionsOutputVideoTemplateBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsOutputVideoTemplate,
);
export const selectAppAdditionalPermissionsIsMediaLive = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isMediaLive || false;

export const selectAppAdditionalPermissionsIsMediaLiveBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsMediaLive,
);

const selectAppAdditionalPermissionsIsFullMatchExport = (appAdditionalPermissions) => {
  return appAdditionalPermissions?.isFullMatchExport ?? false;
};

export const selectAppAdditionalPermissionsIsFullMatchExportBySelectedEntityId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsFullMatchExport,
);
export const selectAppAdditionalPermissionsMultiAudio = (appAdditionalPermissions) =>
  appAdditionalPermissions?.multiAudio || false;

export const selectAppAdditionalPermissionsMultiAudioBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsMultiAudio,
);
export const selectAppAdditionalPermissionsVOD = (appAdditionalPermissions) => appAdditionalPermissions?.vod || false;

export const selectAppAdditionalPermissionsVODBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsVOD,
);
export const selectAppAdditionalPermissionsIsAspectRatioAutoConvert = (appAdditionalPermissions) =>
  appAdditionalPermissions?.isAspectRatioAutoConvert || false;

export const selectAppAdditionalPermissionsIsAspectRatioAutoConvertBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIsAspectRatioAutoConvert,
);
export const selectAppAdditionalPermissionsEnableStreamReplay = (appAdditionalPermissions) =>
  appAdditionalPermissions?.enableStreamReplay || false;

export const selectAppAdditionalPermissionsEnableStreamReplayBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsEnableStreamReplay,
);

export const selectAppAdditionalPermissionsEnableAudioCutChecks = createSelector(
  selectPermissionsBySelectedEntityId,
  (appAdditionalPermissions) => appAdditionalPermissions?.enableAudioCutChecks || false,
);

export const selectAppAdditionalPermissionsEnableAiClipRating = createSelector(
  selectPermissionsBySelectedEntityId,
  (appAdditionalPermissions) => appAdditionalPermissions?.enableAiClipRating || false,
);

export const selectAppAdditionalPermissionsEnableTypesenseSearch = createSelector(
  selectPermissionsBySelectedEntityId,
  (appAdditionalPermissions) => appAdditionalPermissions?.enableTypesenseSearch || false,
);

export const clientSettingMisObject = createSelector(selectEntityConfigDetailsBySelectedEntityId, selectMisObject);

export const selectAppAdditionalPermissionEnableMatchSchedule = createSelector(
  selectPermissionsBySelectedEntityId,
  (appAdditionalPermissions) => appAdditionalPermissions?.enableMatchSchedule || false,
);

export const selectPermissionsAdvanceEditorByEntityId = createSelector(
  selectPermissionsByEntityId,
  (appAdditionalPermissions) => appAdditionalPermissions?.advanceEditor || false,
);

export const selectIllustoAccountSecretByEntityId = createSelector(
  selectEntityConfigDetailsByEntityId,
  (configDetails) => configDetails?.illusto?.accountSecret || "",
);

export const selectIllustoAccountIdByEntityId = createSelector(
  selectEntityConfigDetailsByEntityId,
  (configDetails) => configDetails?.illusto?.accountId || "",
);

const selectAppAdditionalPermissionsIllustoExtended = (appAdditionalPermissions) =>
  appAdditionalPermissions?.illustoExtendedClip || false;

export const selectAppAdditionalPermissionsIllustoExtendedBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsIllustoExtended,
);

export const selectAppAdditionalPermissionsEnableAudioDub = (appAdditionalPermissions) =>
  appAdditionalPermissions?.enableAudioDub || false;

export const selectAppAdditionalPermissionsEnableAudioDubBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsEnableAudioDub,
);

export const selectAppAdditionalPermissionsEnableAllAudioDubLanguages = (appAdditionalPermissions) =>
  appAdditionalPermissions?.enableAllAudioDubLanguages || false;

export const selectAppAdditionalPermissionsEnableAllAudioDubLanguagesBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsEnableAllAudioDubLanguages,
);

export const selectAppAdditionalPermissionsDisableScreenshots = (appAdditionalPermissions) =>
  appAdditionalPermissions?.disableScreenshots || false;

export const selectAppAdditionalPermissionsDisableScreenshotsBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsDisableScreenshots,
);

export const selectAppAdditionalPermissionsAddMagnifiProviderProfile = (appAdditionalPermissions) =>
  appAdditionalPermissions?.addMagnifiProviderProfile || false;

export const selectAppAdditionalPermissionsAddMagnifiProviderProfileBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsAddMagnifiProviderProfile,
);

export const selectAppAdditionalPermissionsEnableImagenPublish = (appAdditionalPermissions) =>
  appAdditionalPermissions?.enableImagenPublish || false;

export const selectAppAdditionalPermissionsEnableImagenPublishBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsEnableImagenPublish,
);

export const selectAppAdditionalPermissionsEnableScoreplayPublish = (appAdditionalPermissions) =>
  appAdditionalPermissions?.enableScoreplayPublish || false;

export const selectAppAdditionalPermissionsEnableScoreplayPublishBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsEnableScoreplayPublish,
);

export const selectAppAdditionalPermissionsAllowPassThroughDownload = (appAdditionalPermissions) =>
  appAdditionalPermissions?.allowPassThroughDownload || false;

export const selectAppAdditionalPermissionsAllowPassThroughDownloadBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsAllowPassThroughDownload,
);

export const selectAppAdditionalPermissionsEnableLiveVideoSegments = (appAdditionalPermissions) =>
  appAdditionalPermissions?.enableLiveVideoSegments || false;

export const selectAppAdditionalPermissionsEnableLiveVideoSegmentsBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsEnableLiveVideoSegments,
);

const selectAppAdditionalPermissionsTimeFormat24Hour = (appAdditionalPermissions) =>
  appAdditionalPermissions?.timeFormat24Hour;

export const selectAppAdditionalPermissionsTimeFormat24HourBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsTimeFormat24Hour,
);

export const selectAppAdditionalPermissionsGraphicsTemplates = (appAdditionalPermissions) =>
  appAdditionalPermissions?.graphicsTemplates || false;

export const selectAppAdditionalPermissionsGraphicsTemplatesBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsGraphicsTemplates,
);

const selectAppAdditionalPermissionsEnableUnmuteLiveVideo = (appAdditionalPermissions) =>
  appAdditionalPermissions?.unmuteLiveVideoPage || false;

export const selectAppAdditionalPermissionsEnableUnmuteLiveVideoBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsEnableUnmuteLiveVideo,
);

export const selectAppAdditionalPermissionsFilmstripGeneration = (appAdditionalPermissions) =>
  appAdditionalPermissions?.filmstripGeneration || false;

export const selectAppAdditionalPermissionsFilmstripGenerationBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsFilmstripGeneration,
);

const selectAppAdditionalPermissionsTimeFormatShowSeconds = (appAdditionalPermissions) =>
  appAdditionalPermissions?.timeFormatShowSeconds;

export const selectAppAdditionalPermissionsTimeFormatShowSecondsBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsTimeFormatShowSeconds,
);

export const selectAppAdditionalPermissionsTimeStampFormatBySelectedUserId = createSelector(
  selectAppAdditionalPermissionsTimeFormat24HourBySelectedUserId,
  selectAppAdditionalPermissionsTimeFormatShowSecondsBySelectedUserId,
  (timeFormat24HourBySelectedUserId, timeFormatShowSecondsBySelectedUserId) => {
    if (timeFormat24HourBySelectedUserId && timeFormatShowSecondsBySelectedUserId) {
      return "MMM DD, YYYY HH:mm:ss";
    }

    if (timeFormat24HourBySelectedUserId) {
      return "MMM DD, YYYY HH:mm";
    }

    if (timeFormatShowSecondsBySelectedUserId) {
      return "MMM DD, YYYY h:mm:ss A";
    }

    return "MMM DD, YYYY h:mm A";
  },
);

export const selectAppAdditionalPermissionsUnpaidInvoiceBanner = (appAdditionalPermissions) =>
  appAdditionalPermissions?.enableUnpaidInvoiceBanner || false;

export const selectAppAdditionalPermissionsUnpaidInvoiceBannerBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsUnpaidInvoiceBanner,
);

export const selectAppAdditionalPermissionsAccountRestriction = (appAdditionalPermissions) =>
  appAdditionalPermissions?.enableAccountRestriction || false;

export const selectAppAdditionalPermissionsAccountRestrictionBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsAccountRestriction,
);

export const selectAppAdditionalPermissionsVideojsPlayerLiveVideo = (appAdditionalPermissions) =>
  appAdditionalPermissions?.videojsPlayerLiveVideo || false;

export const selectAppAdditionalPermissionsVideojsPlayerLiveVideoBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsVideojsPlayerLiveVideo,
);

export const selectAppAdditionalPermissionsWebStoryEditor = (appAdditionalPermissions) =>
  appAdditionalPermissions?.webStory || false;

export const selectAppAdditionalPermissionsWebStoryEditorBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionsWebStoryEditor,
);

export const selectAppAdditionalPermissionsEnablePublishHistoryTypesenseSearch = createSelector(
  selectPermissionsBySelectedEntityId,
  (appAdditionalPermissions) => appAdditionalPermissions?.enablePublishHistoryTypesenseSearch || false,
);

const selectAppAdditionalPermissionAllowShadeIntegration = (appAdditionalPermissions) =>
  appAdditionalPermissions?.shadeIntegration || false;

export const selectAppAdditionalPermissionAllowShadeIntegrationBySelectedUserId = createSelector(
  selectPermissionsBySelectedEntityId,
  selectAppAdditionalPermissionAllowShadeIntegration,
);
